import { ApolloError } from '@apollo/client';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  IPaginationProps,
  LinearProgress,
  LoadedContent,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@occc/ui-design-system';
import { TriggeredMessageListItemFragment } from '@occc/graphql';

import { TriggeredMessagesContextMenu } from './triggered-messages-context-menu';
import { TriggeredMessagesListItem } from './triggered-messages-list-item';

import { routeCreator } from '../../triggered-messages.utils';

type TriggeredMessagesTableProps = {
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  messages?: TriggeredMessageListItemFragment[];
  isUserPermittedToEdit: boolean;
  scope?: string;
};

export const TriggeredMessagesTable = (props: TriggeredMessagesTableProps) => {
  const { loading, error, paginationProps, isUserPermittedToEdit, messages, scope } = props;
  const navigate = useNavigate();

  const handleViewDetailsClick = useCallback(
    (messageUuid: string) => {
      const to = scope
        ? routeCreator.viewScoped.to(scope, messageUuid)
        : routeCreator.view.to(messageUuid);

      navigate(to);
    },
    [navigate, scope],
  );

  const handleEditClick = useCallback(
    (messageUuid: string) => {
      const to = scope
        ? routeCreator.editScoped.to(scope, messageUuid)
        : routeCreator.edit.to(messageUuid);

      navigate(to);
    },
    [navigate, scope],
  );

  const renderData = (items: TriggeredMessageListItemFragment[]) => {
    const rows = items.map((message) => {
      return (
        <TriggeredMessagesListItem
          key={message.uuid}
          message={message}
          onClick={() => {
            handleViewDetailsClick(message.uuid);
          }}
          contextMenu={
            <TriggeredMessagesContextMenu
              messageUuid={message.uuid}
              isUserPermittedToEdit={isUserPermittedToEdit}
              onViewDetailsClick={handleViewDetailsClick}
              onEditClick={handleEditClick}
            />
          }
        />
      );
    });

    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Trigger</TableCell>
              <TableCell>Message Channel</TableCell>
              <TableCell>Status</TableCell>
              {/* table cell for three dots */}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<TriggeredMessageListItemFragment>
      error={error}
      loading={loading}
      items={messages}
      renderData={renderData}
    />
  );
};
